import React from 'react'
import Button from 'library/atoms/Button'
import BottomBar from 'library/atoms/BottomBar'
import { OutletPresenter } from 'shared/presenters/OutletPresenter'
import { SchedulablePresenter } from 'shared/presenters/SchedulablePresenter'
import { OptionalExtraPresenter } from 'shared/presenters/OptionalExtraPresenter'
import { gql, useMutation } from '@apollo/client'
import { useRouter } from 'next/router'
import Summary from 'library/molecules/Summary'
import useTranslation from 'next-translate/useTranslation'
import useAddToBasket from 'state/useAddToBasket'
import { useFormikContext } from 'formik'
import { AddABookingValues } from 'helpers/generateInitialValuesForActivity'
import { definedNotNull } from 'shared/presenters/presenter'
import ModalsAddToBasket, {
  ModalProps,
  MODAL_CONFIG,
  MODAL_IDS
} from 'library/molecules/ModalsAddToBasket'
import useModal from 'shared/state/useModal'
import { determineChargeableReviewDataForSchedulables } from 'helpers/determineChargeableReviewData'
import ChargeableReview from '../ChargeableReview'
import dayjs from 'dayjs'
import formatOptionalExtras, {
  formatChosenOptionalExtrasIntoInputs
} from 'shared/helpers/formatOptionalExtraData/formatOptionalExtras'
import { UseGetPriceForBookingActivity } from 'state/useGetPriceForBookingActivity'
import useRedirectUnauthenticatedOnSchedulableMembersOnly from 'state/useRedirectUnauthenticatedOnSchedulableMembersOnly'
import SchedulablePrice from 'shared/library/atoms/SchedulablePrice'
import widgetRoutes from 'enums/routes'
import useBasket from 'state/useBasket'
import determineOptionalExtrasFromActivityVoucher from 'helpers/determineOptionalExtrasFromActivityVoucher'
import VoucherBar from 'library/molecules/VoucherBar'
import { Mutation_Root } from 'shared/presenters/graphqlTypes'
import { BottomBarContainer } from 'library/atoms/BottomBar/BottomBar'
import { trackEvent } from 'shared/helpers/pageTrackerTools'
import { useEnthusiast } from 'shared/state/useAuth'

export interface ActivityReviewProps
  extends Pick<
    UseGetPriceForBookingActivity,
    'totalPrice' | 'finalPricePence' | 'totalPriceLoading'
  > {
  activity: SchedulablePresenter
  outlet: OutletPresenter
  goToConfig: () => void
  view?: string
}

const ActivityReview = ({
  activity,
  outlet,
  goToConfig,
  totalPrice,
  finalPricePence
}: ActivityReviewProps) => {
  const { push, query } = useRouter()
  const { t } = useTranslation('chargeable')
  const widgetSlug = query.widgetSlug as string
  const { basket, refetchBasket } = useBasket()
  const { authenticated } = useEnthusiast()

  const basketPriceModifiers = basket?.baskets_price_modifiers?.[0]
  const creditVoucher = basketPriceModifiers?.credit_voucher
  const discountCode = basketPriceModifiers?.discount_code

  const shoppingBasketEnabled = outlet?.feature_toggles?.shopping_basket_enabled

  useRedirectUnauthenticatedOnSchedulableMembersOnly({
    schedulableIsMembersOnly: !!activity.members_only
  })

  const bookingFromActivityVoucher =
    basket?.getLineItemBookingFromActivityVoucher()

  const activityVoucherId = bookingFromActivityVoucher?.activity_voucher?.id

  const activityVoucherLineItemId = basket?.getActivityVoucherLineItemId()

  const code = basket?.getActivityVoucherCode()

  const {
    values: {
      adultsGoing: adults,
      childrenGoing: children,
      optionalExtra: optionalExtraFormik,
      exclusive,
      timeSlot,
      organizerParticipating,
      isGift
    }
  } = useFormikContext<AddABookingValues>()

  const standardBooking = !bookingFromActivityVoucher && !isGift

  /**
   *
   * For standard bookings, Organizer Participating should be set to user selection.
   *
   * For purchase as a gift, Organizer Participating should be set to false
   *
   * For redeeming a gift, Organizer Participating should be set to true.
   *
   * For children's only bookings (doesnt allow adults), Organizer Participating should be set to false
   *
   * The logic for setting Organizer Participating to true for redeeming a gift is
   * in rails/app/forms/line_items/activity_voucher_redemption_form.rb.
   *
   * The logic to set it to false for purchase as a gift and the user input on standard booking is below.
   */

  const isOrganizerParticipating = () => {
    if (standardBooking && activity.adultsAllowed()) {
      return organizerParticipating
    } else if (isGift) {
      return false
    } else if (!activity.adultsAllowed()) {
      return false
    }
  }

  const adultsGoing = !bookingFromActivityVoucher
    ? adults
    : (bookingFromActivityVoucher.adultsGoing() ?? 0)

  const childrenGoing = !bookingFromActivityVoucher
    ? children
    : (bookingFromActivityVoucher.childrenGoing() ?? 0)

  const peopleGoing = adults + children

  const optionalExtraPresenter = activity.optional_extras?.map(
    item => new OptionalExtraPresenter(item)
  )

  const chosenOptionalExtrasInputs =
    formatChosenOptionalExtrasIntoInputs(optionalExtraFormik)

  const { addToBasket, addToBasketLoading, addToBasketError } = useAddToBasket({
    chargeable: 'activity'
  })

  const optionalExtras = formatOptionalExtras(
    optionalExtraPresenter,
    optionalExtraFormik
  )

  const optionalExtrasNamesAndQuantitiesFromActivityVoucher =
    determineOptionalExtrasFromActivityVoucher({
      bookingFromActivityVoucher,
      optionalExtraPresenter
    })

  // TODO: #4303
  const timeSlotTimes = !isGift
    ? {
        start: dayjs(timeSlot.start),
        finish: dayjs(timeSlot.finish)
      }
    : undefined

  const chargeableReviewData = determineChargeableReviewDataForSchedulables({
    chargeable: activity,
    adults: adultsGoing,
    children: childrenGoing,
    isGift,
    timeSlot: timeSlotTimes,
    optionalExtras: bookingFromActivityVoucher
      ? optionalExtrasNamesAndQuantitiesFromActivityVoucher
      : optionalExtras,
    exclusive,
    total: totalPrice,
    basket,
    t
  })

  const { activeModal, setActiveModalId, currentModalProps } =
    useModal<ModalProps>({
      MODAL_CONFIG,
      modalPropVariations: {
        addToBasket: {},
        error: {
          onClose: () => setActiveModalId(null),
          errorMessage: addToBasketError?.message
        },
        activeVoucher: {
          lineItemId: activityVoucherLineItemId,
          activityVoucherCode: code,
          basketPriceModifier: basketPriceModifiers ?? {},
          basketId: basket?.id,
          refetchBasket,
          onClose: () => {
            setActiveModalId(null)
          },
          changeViewOnSuccess: () => {
            setActiveModalId(null)
          }
        }
      }
    })

  const handleAddToBasketClick = async () => {
    if (!definedNotNull(activity.id) || (!timeSlot && !isGift)) {
      return
    }

    try {
      await addToBasket({
        variables: {
          schedulableId: activity?.id?.toString(),
          timeSlotGroupId: timeSlot?.id?.toString(),
          adultsGoing: adultsGoing.toString(),
          isGift,
          childrenGoing: childrenGoing.toString(),
          optionalExtrasAndQuantities: chosenOptionalExtrasInputs,
          organizerParticipating: isOrganizerParticipating(),
          exclusive,
          widgetSlug
        },
        onSuccess: () => {
          trackEvent('Basket_conversion', {
            item_type: 'activity',
            item_id: activity?.id,
            outlet_id: outlet?.id,
            widget: widgetSlug,
            enthusiast_logged_in: authenticated
          })
          shoppingBasketEnabled
            ? setActiveModalId(MODAL_IDS.addToBasket)
            : push(widgetRoutes.CHECKOUT.url({ widgetSlug }))
        },
        onError: () => setActiveModalId(MODAL_IDS.error)
      })
    } catch (err) {
      /**
       * Error states are handled in apollo and the useAddToBasket hook so nothing needs to be
       * actioned here. We are using try/catch simply to ensure the modal only opens on success
       */
    }
  }

  const redeemActivityVoucherOnComplete = () => {
    refetchBasket({ basketId: basket?.id })
    shoppingBasketEnabled
      ? setActiveModalId(MODAL_IDS.addToBasket)
      : push(widgetRoutes.CHECKOUT.url({ widgetSlug }))
  }

  const [
    redeemActivityVoucher,
    { loading: redeemVoucherLoading, error: redeemVoucherError }
  ] = useMutation<
    Pick<Mutation_Root, 'Consumer_Baskets_RedeemActivityVoucher'>
  >(ACTIVITY_REVIEW_REDEEM_ACTIVITY_VOUCHER, {
    onCompleted: () => {
      redeemActivityVoucherOnComplete()
    },
    onError: () => {
      setActiveModalId(MODAL_IDS.error)
    }
  })

  const redeemActivityVoucherOnClick = async () => {
    try {
      await redeemActivityVoucher({
        variables: {
          basketId: basket?.id,
          timeSlotGroupId: timeSlot?.id?.toString(),
          activityVoucherId: activityVoucherId,
          widgetSlug
        }
      })
    } catch (err) {
      /**
       * Error states are handled in apollo and the useAddToBasket hook so nothing needs to be
       * actioned here. We are using try/catch simply to ensure the modal only opens on success
       */
    }
  }

  return (
    <>
      {/**
       *
       * Layout
       *
       * */}
      {chargeableReviewData && (
        <ChargeableReview
          {...chargeableReviewData}
          outlet={outlet}
          changeView={goToConfig}
          isMembersOnly={activity?.members_only}
          preAdjustmentsPence={finalPricePence}
          basket={basket}
          bookingFromActivityVoucher={bookingFromActivityVoucher}
          BottomBar={() => (
            <BottomBar padding={0}>
              <VoucherBar
                activity={{ isActive: !!bookingFromActivityVoucher }}
                credit={{ data: creditVoucher }}
                discount={{ data: discountCode }}
                onClick={() => setActiveModalId(MODAL_IDS.activeVoucher)}
              />
              <BottomBarContainer>
                <Summary
                  topText={() => (
                    <SchedulablePrice
                      preAdjustmentsPence={finalPricePence}
                      pence={totalPrice}
                      currency={outlet.currency()}
                      aria-label={t('common:total')}
                    />
                  )}
                  bottomText={t('for-x-people', { count: peopleGoing })}
                />
                <Button
                  onClick={
                    !bookingFromActivityVoucher
                      ? handleAddToBasketClick
                      : redeemActivityVoucherOnClick
                  }
                  inactive={
                    addToBasketLoading ||
                    redeemVoucherLoading ||
                    !!addToBasketError ||
                    !!redeemVoucherError
                  }
                  loading={addToBasketLoading || redeemVoucherLoading}
                >
                  {shoppingBasketEnabled
                    ? t('add-to-basket')
                    : t('common:checkout')}
                </Button>
              </BottomBarContainer>
            </BottomBar>
          )}
        />
      )}
      <ModalsAddToBasket activeModal={activeModal} {...currentModalProps} />
    </>
  )
}

export default ActivityReview

export const ACTIVITY_REVIEW_REDEEM_ACTIVITY_VOUCHER = gql`
  mutation WidgetRedeemActivityVoucher(
    $basketId: ID!
    $timeSlotGroupId: ID!
    $activityVoucherId: ID!
    $enthusiastName: String
    $enthusiastEmail: String
    $contactNumber: String
    $widgetSlug: String!
  ) {
    Consumer_Baskets_RedeemActivityVoucher(
      basket_id: $basketId
      time_slot_group_id: $timeSlotGroupId
      activity_voucher_id: $activityVoucherId
      enthusiast_name: $enthusiastName
      enthusiast_email: $enthusiastEmail
      contact_number: $contactNumber
      widget_slug: $widgetSlug
    ) {
      basket_id
      price_after_adjustments_pence
    }
  }
`
